/*
  QuickBooks Desktop (Web Connector) — READ ONLY (owner decision 9 Oct 2026). The platform reads the company's
  QuickBooks Desktop (invoices + payments, items + quantities, customers + vendors, bills / checks / card charges /
  POs) and shows it NEXT TO its own numbers; nothing in the platform is overwritten and nothing is written to
  QuickBooks. Server: backend/routes/qb_desktop.py, backend/qbxml.py, backend/qbd_match.py.

  API (all under /api/qb-desktop):
    GET  /status                      everyone signed in: {status 'on'|'off'|'not_configured', set_up, company_name,
                                      last_success_at, last_run_at, last_run_status, counts{kind:n}}; admins also get
                                      username, product, company_file, qbxml_version, password_set_at/by, runs[],
                                      last_refusal, locked_out
    POST /password                    admin: {username, password} — shown once
    GET  /qwc                         admin: the Web Connector file (needs the https:// address)
    POST /resync                      admin: the next run reads everything again
    GET  /items                       Inventory: {items:[{qb_id, name, full_name, item_type, is_active, description,
                                      manufacturer_part_number, quantity_on_hand, quantity_on_order, sales_price,
                                      purchase_cost, average_cost, keys[]}], last_success_at}
    GET  /customers, /vendors         Invoicing (vendors: or Procurement)
    GET  /invoice-matches             Invoicing: {matches: {'invoice:<id>'|'variation_invoice:<id>': match}, …}
    GET  /projects/:id                Invoicing: {linked|null, suggestions[] (unlinked) | invoices[], payments[],
                                      matches{}, totals{invoiced, open_balance, received, costs}, costs{…}}
    PUT  /projects/:id/customer       Invoicing edit: {qb_id|null} → {linked, undo_id}
    GET  /project-links               admin: {projects:[{project_id, name, status, linked, suggestions[]}]}

  A match: {qb_id, ref_number, txn_date, customer, subtotal, total, balance_remaining, is_paid, notes[]} — notes are
  codes, words in QB_NOTE_TEXT. {notes:['not_in_quickbooks']} / {ambiguous:n, notes:['ambiguous']} otherwise.
*/
import api from '@/lib/api';

export const QB_DESKTOP_FEATURE = 'quickbooks_desktop';

export const QB_NOTE_TEXT = {
  paid_in_quickbooks: 'Paid in QuickBooks',
  open_in_quickbooks: 'Still open in QuickBooks',
  amount_differs: 'Amount differs from QuickBooks',
  other_customer: 'Another customer in QuickBooks',
  not_in_quickbooks: 'Not in QuickBooks',
  ambiguous: 'Number used more than once in QuickBooks',
};

/** Tone of a match for a StatusPill: green = QuickBooks agrees, amber = look at it, gray = nothing to compare. */
export function qbMatchTone(match) {
  const notes = match?.notes || [];
  if (!match || notes.includes('not_in_quickbooks') || notes.includes('ambiguous')) return 'gray';
  if (notes.length) return 'amber';
  return 'green';
}

/** One short label for a platform invoice's QuickBooks side ("Paid in QuickBooks", "Matches QuickBooks #275", …). */
export function qbMatchLabel(match) {
  const notes = match?.notes || [];
  if (!match) return '';
  if (notes.length) return QB_NOTE_TEXT[notes[0]] || notes[0];
  return match.ref_number ? `Matches QuickBooks #${match.ref_number}` : 'Matches QuickBooks';
}

/** Part numbers compared the way the server does (backend/qbd_match.part_key). */
export const partKey = (text) => String(text ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** part key → QuickBooks item; an inventory item wins over another type, an active one over an inactive one. */
export function itemsByPart(items = []) {
  const rank = (item) => [({ Inventory: 0, Assembly: 1 })[item.item_type] ?? 2, item.is_active === false ? 1 : 0];
  const better = (a, b) => (a[0] - b[0]) || (a[1] - b[1]);
  const out = new Map();
  items.forEach((item) => {
    (item.keys || [partKey(item.name)]).forEach((key) => {
      const current = out.get(key);
      if (!current || better(rank(item), rank(current)) < 0) out.set(key, item);
    });
  });
  return out;
}

/** The QuickBooks data is there to show: switched on and read at least once. */
export const qbDataReady = (status) => status?.status === 'on' && Boolean(status?.last_success_at);

export const fetchQbStatus = () => api.get('/qb-desktop/status').then((r) => r.data);
export const fetchQbItems = () => api.get('/qb-desktop/items').then((r) => r.data);
export const fetchQbCustomers = () => api.get('/qb-desktop/customers').then((r) => r.data);
export const fetchQbVendors = () => api.get('/qb-desktop/vendors').then((r) => r.data);
export const fetchQbInvoiceMatches = () => api.get('/qb-desktop/invoice-matches').then((r) => r.data);
export const fetchQbProject = (projectId) => api.get(`/qb-desktop/projects/${encodeURIComponent(projectId)}`).then((r) => r.data);
export const linkQbCustomer = (projectId, qbId) => api
  .put(`/qb-desktop/projects/${encodeURIComponent(projectId)}/customer`, { qb_id: qbId || null })
  .then((r) => r.data);
export const fetchQbProjectLinks = () => api.get('/qb-desktop/project-links').then((r) => r.data);
export const newQbPassword = () => api.post('/qb-desktop/password').then((r) => r.data);
export const resyncQb = () => api.post('/qb-desktop/resync').then((r) => r.data);
export const fetchQwcFile = () => api.get('/qb-desktop/qwc', { responseType: 'blob' }).then((r) => r.data);
